<template>
  <section class="faq" id="faq">
    <div class="faq__container">
      <!-- Section Header -->
      <div class="faq__header">
        <span class="faq__badge">
          <i class="pi pi-question-circle"></i>
          FAQ
        </span>
        <h2 class="faq__title">
          Preguntas <span>frecuentes</span>
        </h2>
        <p class="faq__description">
          Todo lo que necesitas saber sobre ThunderLabs y la validación de emails.
        </p>
      </div>
      
      <!-- FAQ Grid -->
      <div class="faq__grid">
        <div class="faq__column">
          <div 
            class="faq__item"
            v-for="(item, index) in faqsLeft"
            :key="index"
          >
            <button 
              class="faq__question"
              :class="{ 'is-open': openIndex === index }"
              @click="toggleFaq(index)"
            >
              <span>{{ item.question }}</span>
              <i :class="openIndex === index ? 'pi pi-minus' : 'pi pi-plus'"></i>
            </button>
            <transition name="faq-slide">
              <div class="faq__answer" v-show="openIndex === index">
                <p>{{ item.answer }}</p>
              </div>
            </transition>
          </div>
        </div>
        
        <div class="faq__column">
          <div 
            class="faq__item"
            v-for="(item, index) in faqsRight"
            :key="index + faqsLeft.length"
          >
            <button 
              class="faq__question"
              :class="{ 'is-open': openIndex === index + faqsLeft.length }"
              @click="toggleFaq(index + faqsLeft.length)"
            >
              <span>{{ item.question }}</span>
              <i :class="openIndex === index + faqsLeft.length ? 'pi pi-minus' : 'pi pi-plus'"></i>
            </button>
            <transition name="faq-slide">
              <div class="faq__answer" v-show="openIndex === index + faqsLeft.length">
                <p>{{ item.answer }}</p>
              </div>
            </transition>
          </div>
        </div>
      </div>
      
      <!-- Contact CTA -->
      <div class="faq__contact">
        <p class="faq__contact-text">
          ¿No encuentras lo que buscas?
        </p>
        <Button 
          label="Contactar soporte"
          class="p-button-outlined faq__contact-button"
          icon="pi pi-envelope"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import Button from 'primevue/button';

const openIndex = ref(null);

const faqs = [
  {
    question: '¿Qué es la validación de emails?',
    answer: 'La validación de emails es el proceso de verificar si una dirección de correo electrónico existe, está activa y puede recibir mensajes. Esto incluye verificación de sintaxis, DNS, registros MX y conexión SMTP directa al servidor de correo.'
  },
  {
    question: '¿Cómo funciona la validación SMTP?',
    answer: 'Nos conectamos directamente al servidor de correo del destinatario y simulamos el envío de un email (sin enviarlo realmente). El servidor responde confirmando si el buzón existe o no, permitiéndonos validar con 99% de precisión.'
  },
  {
    question: '¿Qué es un email "catch-all"?',
    answer: 'Un servidor catch-all acepta todos los emails enviados a su dominio, incluso si el buzón no existe. Esto dificulta la validación, pero nuestro sistema detecta estos dominios y los marca como "riesgosos" para que puedas decidir si enviar o no.'
  },
  {
    question: '¿Cuánto tiempo toma validar mi lista?',
    answer: 'Depende del tamaño de tu lista. Una lista de 10,000 emails típicamente se valida en menos de 5 minutos. Listas más grandes pueden tomar entre 15-30 minutos. Te notificamos por email cuando esté lista.'
  },
  {
    question: '¿Es seguro subir mi lista de emails?',
    answer: 'Absolutamente. Cumplimos con GDPR, SOC2 Type II y utilizamos encriptación de 256 bits. Tus datos nunca se comparten con terceros y puedes eliminarlos en cualquier momento desde tu panel.'
  },
  {
    question: '¿Puedo integrar ThunderLabs con mi CRM?',
    answer: 'Sí, ofrecemos integraciones nativas con HubSpot, Salesforce, Mailchimp, ActiveCampaign y más de 50 plataformas. También tenemos una API REST completa para integraciones personalizadas.'
  },
  {
    question: '¿Qué pasa si no estoy satisfecho?',
    answer: 'Ofrecemos una garantía de satisfacción de 30 días. Si por cualquier motivo no estás contento con el servicio, te devolvemos el 100% de tu dinero sin preguntas.'
  },
  {
    question: '¿Puedo probar antes de pagar?',
    answer: 'Sí, ofrecemos 1,000 validaciones gratuitas al registrarte. No necesitas tarjeta de crédito para comenzar. Prueba el servicio completo antes de decidir.'
  }
];

const faqsLeft = computed(() => faqs.slice(0, Math.ceil(faqs.length / 2)));
const faqsRight = computed(() => faqs.slice(Math.ceil(faqs.length / 2)));

const toggleFaq = (index) => {
  openIndex.value = openIndex.value === index ? null : index;
};
</script>

<style lang="scss" scoped>
@import '@/design-system/variables';

.faq {
  padding: $tl-section-spacing 0;
  background: $tl-white;
}

.faq__container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 $tl-space-6;
}

// Header
.faq__header {
  text-align: center;
  max-width: 600px;
  margin: 0 auto $tl-space-12;
}

.faq__badge {
  display: inline-flex;
  align-items: center;
  gap: $tl-space-2;
  padding: $tl-space-2 $tl-space-4;
  background: rgba($tl-primary, 0.1);
  color: $tl-primary;
  border-radius: $tl-radius-full;
  font-size: $tl-font-size-sm;
  font-weight: $tl-font-weight-medium;
  margin-bottom: $tl-space-4;
}

.faq__title {
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: $tl-font-weight-bold;
  color: $tl-jet;
  margin-bottom: $tl-space-4;
  line-height: 1.2;
  
  span {
    color: $tl-primary;
  }
}

.faq__description {
  font-size: $tl-font-size-lg;
  color: $tl-davys-gray;
  line-height: 1.6;
}

// Grid
.faq__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $tl-space-6;
  margin-bottom: $tl-space-12;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.faq__column {
  display: flex;
  flex-direction: column;
  gap: $tl-space-4;
}

// Item
.faq__item {
  background: $tl-gray-50;
  border-radius: $tl-radius-lg;
  overflow: hidden;
  border: 1px solid $tl-gray-100;
  transition: all 0.3s ease;
  
  &:has(.is-open) {
    border-color: $tl-primary;
    box-shadow: 0 4px 20px rgba($tl-primary, 0.1);
  }
}

.faq__question {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $tl-space-4;
  padding: $tl-space-5;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  
  span {
    font-size: $tl-font-size-base;
    font-weight: $tl-font-weight-medium;
    color: $tl-jet;
    line-height: 1.4;
  }
  
  i {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $tl-gray-200;
    border-radius: 50%;
    font-size: 0.75rem;
    color: $tl-jet;
    transition: all 0.3s ease;
  }
  
  &.is-open {
    i {
      background: $tl-primary;
      color: $tl-white;
    }
  }
  
  &:hover {
    i {
      background: $tl-primary;
      color: $tl-white;
    }
  }
}

.faq__answer {
  padding: 0 $tl-space-5 $tl-space-5;
  
  p {
    font-size: $tl-font-size-sm;
    color: $tl-davys-gray;
    line-height: 1.7;
    margin: 0;
  }
}

// Transition
.faq-slide-enter-active,
.faq-slide-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
}

.faq-slide-enter-from,
.faq-slide-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

// Contact CTA
.faq__contact {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $tl-space-4;
  padding: $tl-space-6;
  background: $tl-gray-50;
  border-radius: $tl-radius-xl;
  
  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
}

.faq__contact-text {
  font-size: $tl-font-size-base;
  color: $tl-davys-gray;
  margin: 0;
}

.faq__contact-button {
  border-color: $tl-primary !important;
  color: $tl-primary !important;
  
  &:hover {
    background: rgba($tl-primary, 0.1) !important;
  }
}
</style>
